import { createContext, FC, ReactNode, startTransition, useContext, useEffect, useRef, useState } from "react";
import { scheduleIdle } from "@/lib/defer";
import { useNearViewport } from "src/hooks/useNearViewport";

/**
 * True when the page was server-rendered for a browser rather than a crawler (src/lib/crawler.ts),
 * so blocks nobody sees at first paint are left out of the server HTML and drawn in the browser.
 * Provided by RootLayout from the stock pages' `fullRender` prop. Off by default: every other page,
 * and every page without the prop, renders as it always has.
 */
export const DeferHiddenBlocksContext = createContext(false);

interface Props {
    /**
     * When to draw the block in a browser render:
     * - "hydrated": just after hydration, as a transition (time-sliced, out of hydration's long task).
     *   For blocks that may be needed soon but are hidden at first paint (the tab strip is hidden
     *   until the user has loaded).
     * - "idle": once the browser is first idle (at most 2 s), or as soon as the block comes within
     *   600 px of the visible part of the page, whichever is first. For blocks below the fold.
     */
    when: "hydrated" | "idle";
    children: ReactNode;
}

/**
 * Draws `children` in the server HTML and hydrates them as before, unless the page is a browser
 * render (`DeferHiddenBlocksContext`); then it draws nothing on the server and in the hydrating
 * render, and the children once `when` says so. The children are the same either way, in the same
 * place, so the final page is the same: only when it is drawn differs. Once drawn, they stay.
 *
 * Only for blocks that are invisible at first paint (hidden, or below the fold with nothing after
 * them that is in view), so drawing them later cannot shift what the reader sees.
 */
const DeferredMount: FC<Props> = ({ when, children }) => {
    const defer = useContext(DeferHiddenBlocksContext);
    const [ready, setReady] = useState(!defer);
    const marker = useRef<HTMLSpanElement>(null);
    const near = useNearViewport(marker, { margin: "600px", enabled: !ready && when === "idle" });

    useEffect(() => {
        if (ready) return;
        const show = () => startTransition(() => setReady(true));
        if (when === "hydrated" || near) {
            show();
            return;
        }
        return scheduleIdle(show);
    }, [ready, when, near]);

    if (ready) return <>{children}</>;
    // An empty marker for the viewport watch: no size, so it moves nothing around it.
    return when === "idle" ? <span ref={marker} aria-hidden="true" style={{ display: "block", height: 0 }} /> : null;
};

export default DeferredMount;
